import {ContentTileWithTitle} from '#/atoms/content-tile';
import {LoadingDots} from '#/atoms/dots-loading-indicator';
import {InfoTriggerTooltip} from '#/atoms/info-trigger-tooltip';
import {WarningMessageAction} from '#/atoms/warning-message-action';
import GraphRangeSelector, {GraphRangeContext, GraphRangeProvider} from '#/components/graphs/graph-range-selector';
import {MarketValueMinMaxIndicatorGraph} from '#/components/graphs/portfolio-market-value-graph';
import {MobileBalanceCard} from '#/components/header/balance-card';
import {MarketIndicatorIcon} from '#/components/indicators/market-indicator-icon';
import {PortfolioAccountContext} from '#/components/portfolio/context/portfolio-account-context';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {Portfolio} from '#/hooks/use-portfolio';
import {PortfolioValueGraphData, usePortfolioValueGraphData} from '#/hooks/use-portfolio-value-graph-data';
import {decimalsMultiplier} from '#/utils/contract-consts';
import {ClassName} from '#/utils/utils';
import {GraphRange} from '@shareland/shareland-common';
import {UseQueryResult} from '@tanstack/react-query';
import {FunctionComponent, useContext} from 'react';
import {useTranslation} from 'react-i18next';
import {Hex} from 'viem';

export const PortfolioValue: FunctionComponent<ClassName> = ({className}) => {
  const {account} = useContext(PortfolioAccountContext);
  const {t} = useTranslation('translation');

  return (
    <div
      className={`h-full relative bg-graybluedark rounded-2xl flex flex-col justify-center gap-2 overflow-hidden ${className}`}
    >
      <ContentTileWithTitle
        title={
          <div className='w-full flex justify-between items-center'>
            <span>{t('pages.portfolio.sections.info.sections.value.title')}</span>
            <span className='normal-case'>
              <InfoTriggerTooltip tooltipId={'portfolio-value-tooltip'}>
                {t('pages.portfolio.sections.info.sections.value.valueExplanationTooltip')}
              </InfoTriggerTooltip>
            </span>
          </div>
        }
      >
        <GraphRangeProvider>
          <GraphRangeContext.Consumer>
            {({graphRange}) => (
              <div className='w-full relative space-y-4'>
                <ValueGraphGroup graphRange={graphRange} account={account} />
                <GraphRangeSelector />
              </div>
            )}
          </GraphRangeContext.Consumer>
        </GraphRangeProvider>
      </ContentTileWithTitle>
    </div>
  );
};

export const PortfolioBalance = () => {
  const {t} = useTranslation('translation');
  return (
    <ContentTileWithTitle
      title={
        <div>
          <span>{t('pages.portfolio.sections.balance.title')}</span>
        </div>
      }
    >
      <MobileBalanceCard />
    </ContentTileWithTitle>
  );
};

const getDelta = (graphData: PortfolioValueGraphData[]) =>
  !!graphData && graphData.length > 0 ? graphData[graphData.length - 1].marketValue - graphData[0].marketValue : 0;

type DisplayMode = 'loading' | 'error' | 'empty' | 'data';
const getGraphDisplayMode = (
  graphData: PortfolioValueGraphData[],
  status: UseQueryResult['status'],
  portfolioIsLoading: boolean,
): DisplayMode => {
  if (status === 'pending' || portfolioIsLoading) {
    return 'loading';
  }
  if (status === 'error') {
    return 'error';
  }
  if (graphData.length === 0) {
    return 'empty';
  }
  return 'data';
};

export const ValueGraphGroup: FunctionComponent<{
  graphRange: GraphRange;
  account: Hex;
}> = ({account, graphRange}) => {
  const {portfolioQuery} = useContext(PortfolioAccountContext);
  const {data: graphData, status, refetch} = usePortfolioValueGraphData(graphRange, account, portfolioQuery.data);

  const displayMode = getGraphDisplayMode(graphData, status, portfolioQuery.isLoading);

  return (
    <>
      <ValueChangeIndicator
        portfolio={portfolioQuery.data}
        delta={displayMode === 'data' ? getDelta(graphData) : undefined}
      />
      <div className='h-[160px]'>
        <LoadingErrorEmptyDataGraph graphData={graphData} refetch={refetch} displayMode={displayMode} />
      </div>
    </>
  );
};
const ValueChangeIndicator: FunctionComponent<{
  portfolio: Portfolio;
  delta: number | undefined;
}> = ({portfolio, delta}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();

  return (
    <div className='flex gap-1'>
      <div className='flex items-center text-white text-lg font-bold gap-1'>
        {t('utils.stableTokenAmount', {
          currencySymbol: displayPref.symbol,
          amount: displayPref.displayAmount(portfolio.totalAssetsValue),
        })}

        {delta !== undefined && (
          <>
            <MarketIndicatorIcon
              className={'ml-2 mr-0.5'}
              size={'sm'}
              direction={delta === 0 ? 'stable' : delta > 0 ? 'up' : 'down'}
            />
            <span className='data-[stable=true]:text-gray-400' data-stable={delta === 0}>
              {t('utils.stableAmount', {
                amount: displayPref.displayAmount(delta * decimalsMultiplier),
              })}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

const LoadingErrorEmptyDataGraph: FunctionComponent<
  {
    graphData: PortfolioValueGraphData[];
    refetch: () => void;
    displayMode: DisplayMode;
  } & ClassName
> = ({graphData, refetch, displayMode}) => {
  const {t} = useTranslation('translation');

  if (displayMode === 'loading') {
    return (
      <div className='w-full h-full flex flex-col justify-center items-center'>
        <LoadingDots widthClassName='w-3' gapClassName='gap-2' />
      </div>
    );
  }

  if (displayMode === 'error') {
    return (
      <WarningMessageAction
        className='h-full text-sm text-white font-semibold'
        iconStrokeClassName='stroke-downred'
        message={t('components.warningMessageAction.errorLoadingData.message')}
        buttonContent={t('components.warningMessageAction.errorLoadingData.cta')}
        action={refetch}
      />
    );
  }

  if (displayMode === 'empty') {
    return (
      <WarningMessageAction
        className='h-full text-sm text-white font-semibold'
        iconStrokeClassName='stroke-deepblue'
        message={t('components.warningMessageAction.noDataToShowForSelectedRange.message')}
        buttonContent={t('components.warningMessageAction.noDataToShowForSelectedRange.cta')}
        action={refetch}
      />
    );
  }

  return <MarketValueMinMaxIndicatorGraph graphData={graphData} />;
};
